import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Award,
  Users,
  Clock,
  ThumbsUp,
  MapPin,
  Star,
} from "lucide-react";

const testimonials = [
  {
    id: 1,
    name: "Mayank Sahani",
    rating: 5,
    text: "Shyam Advertising nailed it! Their creativity and attention to detail are top-notch. The team was super professional, delivered on time, and exceeded expectations. Highly recommend for anyone looking for impactful ad solutions!",
  },
  {
    id: 2,
    name: "Ankit Singh",
    rating: 5,
    text: "Very Nice Service At Affordable Price. Also The Team Is Very Good And Supportive. I'll Extremely Recommend Shyam Advertising.",
  },
  {
    id: 3,
    name: "Rajesh Shukla",
    rating: 5,
    text: "Your service is best. I like it soo much. Be the best in all world. You can go very high. I wish you will be very best. Your service are sooo good 😊😊",
  },
  {
    id: 4,
    name: "Vedaant Bhandary",
    rating: 5,
    text: "Great service and even better people. Had a nice experience dealing with them.",
  },
  {
    id: 5,
    name: "Vijay",
    rating: 5,
    text: "Good job work... enough options available. High quality reasonable price.",
  },
];

function BusinessInfo() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length,
    );
  };

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, currentIndex]);

  const stats = [
    {
      id: 1,
      icon: <Award className="w-8 h-8 text-brand-amber" />,
      number: "25+",
      label: "Years of excellence",
      description: "Serving Mumbai with quality signage",
    },
    {
      id: 2,
      icon: <Users className="w-8 h-8 text-brand-amber" />,
      number: "4.7",
      label: "Google rating",
      description: "Trusted by businesses across Mumbai",
    },
    {
      id: 3,
      icon: <Clock className="w-8 h-8 text-brand-amber" />,
      number: "24/7",
      label: "Support available",
      description: "Round the clock customer service",
    },
    {
      id: 4,
      icon: <ThumbsUp className="w-8 h-8 text-brand-amber" />,
      number: "5000+",
      label: "Projects completed",
      description: "From small shops to corporate brands",
    },
  ];

  const features = [
    {
      id: 1,
      title: "Premium quality",
      description: "High-grade materials and precision craftsmanship",
    },
    {
      id: 2,
      title: "Custom designs",
      description: "Tailored solutions for your brand identity",
    },
    {
      id: 3,
      title: "Timely delivery",
      description: "On-time project completion guaranteed",
    },
    {
      id: 4,
      title: "Justified cost",
      description: "Factory-direct value without compromising finish",
    },
  ];

  return (
    <section className="relative py-16 md:py-24 bg-surface overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.4] pointer-events-none bg-[radial-gradient(ellipse_100%_80%_at_50%_0%,rgba(234,88,12,0.12),transparent_55%)]"
        aria-hidden
      />
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <p className="text-brand-amber text-sm uppercase tracking-[0.18em] font-semibold mb-3">
            Why brands trust us
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            Values first,
            <br />
            <span className="text-gradient-brand">money follows</span>
          </h2>
          <p className="text-gray-400 text-base md:text-lg">
            In-house design and manufacturing mean sharper details, honest
            pricing, and faster delivery—so your brand looks as good on the
            facade as it does in the mockup.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-16">
          <article className="rounded-2xl bg-surface-elevated/80 border border-white/10 p-8 md:p-10 hover:border-brand-amber/25 transition-colors">
            <h3 className="text-3xl md:text-4xl font-bold text-gradient-brand mb-4">
              Quality
            </h3>
            <p className="text-gray-400 leading-relaxed mb-4">
              Years on the shop floor taught us precision matters—from LED
              uniformity to paint finish and weather sealing. We prototype,
              fabricate, and install with the same team, so nothing gets lost
              between departments.
            </p>
            <p className="text-gray-500 text-sm">
              Visit our experience centre for live samples, or browse the
              catalog to shortlist finishes before you commit.
            </p>
          </article>

          <article className="rounded-2xl bg-surface-elevated/80 border border-white/10 p-8 md:p-10 hover:border-brand-amber/25 transition-colors">
            <h3 className="text-3xl md:text-4xl font-bold text-gradient-brand mb-4">
              Justified cost
            </h3>
            <p className="text-gray-400 leading-relaxed mb-4">
              An owned manufacturing setup cuts middlemen and keeps budgets on
              the brief. Bulk material purchases and smart production scheduling
              help us stay competitive without cutting corners on materials.
            </p>
            <p className="text-gray-500 text-sm">
              Ask for a line-item estimate—clear specs, clear quantities, no
              surprise add-ons.
            </p>
          </article>
        </div>

        {/* Testimonials Slider Section */}
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-brand-orange/10 via-surface-elevated to-brand-yellow/5 p-8 md:p-10 mb-16">
          <div className="text-center mb-8">
            <p className="text-brand-yellow font-semibold text-sm uppercase tracking-wider mb-2">
              What customers think
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
              Real projects. Real feedback.
            </h3>
            <p className="text-gray-400 max-w-xl mx-auto">
              Most clients find us through referrals and repeat orders. Check
              our project gallery to see how we handle timelines and finishes.
            </p>
            <div className="flex items-center justify-center gap-2 mt-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star
                  key={i}
                  className="w-5 h-5 fill-brand-yellow text-brand-yellow"
                  aria-hidden
                />
              ))}
              <span className="text-gray-400 text-sm ml-2">4.7 on Google</span>
            </div>
          </div>

          {/* Slider */}
          <div className="relative max-w-2xl mx-auto">
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {testimonials.map((testimonial) => (
                  <div
                    key={testimonial.id}
                    className="w-full flex-shrink-0 px-4"
                  >
                    <div className="bg-surface-elevated/80 rounded-2xl p-6 md:p-8 border border-white/5 max-w-md mx-auto">
                      <div>
                        <h4 className="text-white font-semibold text-lg mb-2 text-center">
                          {testimonial.name}
                        </h4>
                        <div className="flex items-center justify-center gap-1 mb-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star
                              key={i}
                              className="w-4 h-4 fill-brand-yellow text-brand-yellow"
                            />
                          ))}
                        </div>
                        <p className="text-gray-300 text-base md:text-lg leading-relaxed text-center">
                          {testimonial.text}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-4 bg-black/50 hover:bg-brand-amber/80 rounded-full p-2 transition-all duration-300 backdrop-blur-sm"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-4 bg-black/50 hover:bg-brand-amber/80 rounded-full p-2 transition-all duration-300 backdrop-blur-sm"
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-6">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentIndex(index);
                    setIsAutoPlaying(false);
                    setTimeout(() => setIsAutoPlaying(true), 5000);
                  }}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    currentIndex === index
                      ? "w-8 bg-brand-amber"
                      : "w-2 bg-white/30 hover:bg-white/50"
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="text-center mt-8">
            <Link
              to="/projects"
              className="inline-flex items-center justify-center rounded-full bg-gradient-brand px-6 py-3 text-sm font-bold text-black hover:opacity-95 transition-all"
            >
              View real projects
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
          {stats.map((stat) => (
            <div
              key={stat.id}
              className="text-center p-6 rounded-2xl border border-white/10 bg-surface-elevated/60 hover:border-brand-amber/30 transition-all duration-300"
            >
              <div className="flex justify-center mb-3">{stat.icon}</div>
              <div className="text-3xl md:text-4xl font-bold text-white">
                {stat.number}
              </div>
              <div className="text-brand-amber font-semibold mt-1">
                {stat.label}
              </div>
              <div className="text-gray-500 text-sm mt-2">
                {stat.description}
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {features.map((feature) => (
            <div
              key={feature.id}
              className="flex items-start gap-3 p-4 rounded-xl hover:bg-white/[0.03] transition-colors"
            >
              <div className="w-1 h-full min-h-[3rem] shrink-0 rounded-full bg-gradient-to-b from-brand-orange to-brand-yellow" />
              <div>
                <h3 className="text-white font-semibold mb-1">
                  {feature.title}
                </h3>
                <p className="text-gray-500 text-sm">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <div className="inline-flex items-center gap-2 flex-wrap justify-center bg-surface-elevated/90 px-6 py-3.5 rounded-full border border-white/10">
            <MapPin className="w-5 h-5 text-brand-amber shrink-0" />
            <span className="text-gray-400 text-sm text-left">
              Based in{" "}
              <span className="text-gradient-brand font-semibold">
                Kandivali East, Mumbai
              </span>{" "}
              — serving the entire Mumbai Metropolitan Region
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default BusinessInfo;
